import React from 'react';
import PropTypes from 'prop-types';
import { Field, useField } from 'formik';

import styled, { css } from 'styled-components';

import BaseIconPath from '../assets/spot.svg?react';
import ErrorIconPath from '../assets/stroked-circle-cross.svg?react';
import SuccessIconPath from '../assets/stroked-circle-check.svg?react';
import WarningIconPath from '../assets/stroke-circle-warning.svg?react';

const InputStatus = {
    default: {
        color: 'inherit',
        borderColor: 'var(--gray200)',
        backgroundColor: 'var(--color-white)',
    },
    touched: {
        color: 'var(--gray800)',
        borderColor: 'var(--gray800)',
    },
    hover: {
        borderColor: 'var(--gray800)',
        backgroundColor: 'var(--gray50)',
    },
    focus: {
        boxShadowColor: 'var(--blue200)',
        borderColor: 'var(--text-grey-05)',
    },
};

const Validation = {
    error: {
        backgroundColor: 'var(--red50)',
        borderColor: 'var(--color-error-default)',
        labelColor: 'var(--color-error-default)',
    },
    default: {
        backgroundColor: 'var(--color-white)',
        borderColor: 'var(--gray200)',
        labelColor: 'var(--gray700)',
    },
    success: {
        backgroundColor: 'var(--green50)',
        borderColor: 'var(--color-success-default)',
        labelColor: 'var(--color-success-default)',
    },
    warning: {
        backgroundColor: 'var(--yellow50)',
        borderColor: 'var(--color-warning-default)',
        color: 'var(--color-warning-default)',
        labelColor: 'var(--yellow800)',
    },
};

const HelperContainer = styled.div`
    align-items: center;
    display: flex;
    margin: 8px 0 8px 0;
`;

export const Helper = styled.div`
    margin-top: 0;
    font-size: 14px;
    display: flex;
    color: ${(props) => Validation[props.validation].labelColor};
    height: 16px;
`;

export const IconContainer = styled.div`
    height: 16px;
    right: 6px;
    width: 16px;

    margin-right: 8px;
`;

export const BaseIcon = styled(BaseIconPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['text-grey-700']};

        &[disabled] {
            fill: ${theme.colors['text-grey-700']};
        }
    `}
`;

export const SuccessIcon = styled(SuccessIconPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['text-success-green-main']};

        &[disabled] {
            fill: ${theme.colors['text-grey-700']};
        }
    `}
`;

export const ErrorIcon = styled(ErrorIconPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['text-error-red-main']};

        &[disabled] {
            fill: ${theme.colors['text-grey-700']};
        }
    `}
`;

export const WarningIcon = styled(WarningIconPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['text-warning-yellow-dark']};

        &[disabled] {
            fill: ${theme.colors['text-grey-700']};
        }
    `}
`;

export const InputStyled = styled.input`
    padding: 10px 12px;
    box-sizing: border-box;
    background: inherit;
    border: none;
    color: inherit;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 16px;
    height: 100%;
    line-height: 24px;
    outline: none;
    width: 100%;
`;

export const InputWrapper = styled.div`
    box-sizing: border-box;
    background-color: ${(props) => Validation[props.validation].backgroundColor};
    border: 1px solid ${(props) => Validation[props.validation].borderColor};
    box-sizing: border-box;
    color: ${(props) => (props.disabled ? 'var(--gray700)' : 'var(--gray800)')};
    margin-top: 8px;
    outline: none;
    position: relative;
    min-height: 44px;

    ${(props) =>
        props.validation === 'default' &&
        !props.disabled &&
        !props.open &&
        css`
            &:hover {
                border: 1px solid ${InputStatus['hover'].borderColor};
                background-color: ${InputStatus['hover'].backgroundColor};
            }

            &:focus-within {
                box-shadow: 0px 0px 2px 2px ${InputStatus['focus'].boxShadowColor};
            }
        `}

    ${(props) =>
        props.touched &&
        props.validation === 'default' &&
        !props.disabled &&
        css`
            color: ${InputStatus['touched'].color};
            border: 1px solid ${InputStatus['touched'].borderColor};
        `}
`;

export const Label = styled.div`
    color: var(--gray800);
    font-size: 12px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.32px;
    line-height: 16px;
    align-self: stretch;
`;

export const LabelWrapper = styled.div`
    align-items: flex-start;
    display: flex;
    height: 16px;
    justify-content: space-between;
    margin-top: 8px;
    width: 100%;
`;

export const Link = styled.a`
    color: var(--blue500);
    height: 16px;
    font-size: 12px;
    font-family: 'IBM Plex Sans', sans-serif;
    letter-spacing: 0.32px;
    line-height: 16px;
    align-self: flex-end;
    text-decoration: none;
`;

export const TextArea = styled.textarea`
    padding: 10px 12px;
    box-sizing: border-box;
    background: inherit;
    border: none;
    color: inherit;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 16px;
    height: 100%;
    min-height: 92px;
    line-height: 24px;
    outline: none;
    width: 100%;
    resize: none;
`;

const Input = ({ helper, label, linkLabel, linkUrl, onChange, placeholder, textarea, validation = 'default', ...props }) => {
    const [field, meta] = useField(props);

    const badgeContent = () => {
        switch (validation) {
            case 'error':
                return <ErrorIcon disabled={props.disabled} />;
            case 'success':
                return <SuccessIcon disabled={props.disabled} />;
            case 'warning':
                return <WarningIcon disabled={props.disabled} />;
            default:
                return <BaseIcon disabled={props.disabled} />;
        }
    };

    return (
        <>
            {label && (
                <LabelWrapper>
                    <Label>{label}</Label>
                    <Link target="_blank" href={linkUrl}>
                        {linkLabel}
                    </Link>
                </LabelWrapper>
            )}
            <InputWrapper validation={validation} disabled={props.disabled} touched={meta.touched}>
                <Field
                    {...field}
                    as={textarea ? TextArea : InputStyled}
                    autoComplete="off"
                    disabled={props.disabled}
                    onChange={(e) => onChange(e)}
                    placeholder={placeholder}
                    tabIndex={0}
                />
            </InputWrapper>
            <HelperContainer>
                {validation !== 'default' && <IconContainer>{badgeContent()}</IconContainer>}
                <Helper validation={validation}>{helper}</Helper>
            </HelperContainer>
        </>
    );
};

export default Input;

Input.propTypes = {
    disabled: PropTypes.bool,
    helper: PropTypes.string,
    label: PropTypes.string,
    linkLabel: PropTypes.string,
    linkUrl: PropTypes.string,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    textarea: PropTypes.bool,
    validation: PropTypes.oneOf(['active', 'error', 'default', 'success', 'warning']),
};
